import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Info, Heart, ScanLine, LogIn, LogOut, Flame } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { ListCard } from "@/components/cards";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { api } from "@/lib/api";
import { getStreak } from "@/lib/streak";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function History() {
  const { user, loading, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const streak = getStreak();

  const load = () => api.get("/scans").then((r) => setScans(r.data)).catch(() => {});
  useEffect(() => { if (user) load(); }, [user]);

  const clearAll = async () => {
    await api.delete("/scans").catch(() => {});
    setScans([]); setConfirm(false); toast.success(t("history.cleared"));
  };

  return (
    <AppShell>
      <div className="flex items-center justify-between">
        <button onClick={() => navigate("/favorites")} data-testid="favorites-link" className="text-emerald-600 font-semibold flex items-center gap-1">
          <Heart className="w-4 h-4" /> {t("history.favorites")}
        </button>
        <div className="flex items-center gap-3">
          {user && scans.length > 0 && (
            <button onClick={() => setConfirm(true)} data-testid="clear-history-button" className="text-emerald-600"><Trash2 className="w-5 h-5" /></button>
          )}
          {user
            ? <button onClick={() => { logout(); toast(t("common.logout")); }} data-testid="logout-icon" className="text-slate-400"><LogOut className="w-5 h-5" /></button>
            : <button onClick={() => toast.info(t("history.infoTip"))} className="text-emerald-600"><Info className="w-5 h-5" /></button>}
        </div>
      </div>
      <h1 className="font-heading text-4xl font-extrabold text-slate-900 mt-3 mb-4">{t("history.title")}</h1>

      {streak.total > 0 && (
        <div className="flex items-center gap-3 rounded-2xl bg-[#FBF8F2] border border-black/5 p-4 mb-5 shadow-[0_2px_8px_rgba(60,50,35,0.06)]" data-testid="streak-card">
          <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
            <Flame className={`w-6 h-6 ${streak.current > 0 ? "text-orange-500" : "text-slate-300"}`} strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <p className="font-heading text-2xl font-extrabold text-slate-900 leading-none">
              {streak.current}<span className="text-base font-semibold text-slate-500"> {t("streak.daysWord")}</span>
            </p>
            <p className="text-sm text-slate-500 mt-1">{streak.current > 0 ? t("streak.subtitle") : t("streak.broken")}</p>
          </div>
        </div>
      )}

      {!user ? (
        <div className="text-center py-16 px-6">
          <ScanLine className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-500 mb-6">{t("history.loginPrompt")}</p>
          <Button onClick={() => navigate("/auth")} data-testid="history-login-button" className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-12 px-8">
            <LogIn className="w-4 h-4 mr-2" /> {t("common.login")}
          </Button>
        </div>
      ) : scans.length === 0 ? (
        <div className="text-center py-16 px-6" data-testid="history-empty">
          <ScanLine className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 mb-6">{t("history.emptyTitle")}</p>
          <Button onClick={() => navigate("/scan")} className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-12 px-8">{t("history.scanBtn")}</Button>
        </div>
      ) : (
        <div>{scans.map((s) => <ListCard key={s.barcode} item={s} time={s.updated_at} />)}</div>
      )}

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="rounded-3xl max-w-[360px]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("history.clearTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("history.clearDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={clearAll} data-testid="confirm-clear-history" className="rounded-full bg-red-600 hover:bg-red-700">{t("history.clear")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
